import React, {
  ChangeEvent,
  CSSProperties,
  FC,
  useEffect,
  useState,
} from 'react';
import { conformToMask } from 'text-mask-core';
import { SmsCountry } from '../../../../../../../__generated__/loma';
import TextInput, {
  SupportedInputType,
} from '../../../../ui/TextInput/TextInput';
import Select, { SelectOptionProps } from '../../../../ui/Select/BlockSelect';
import { usePageContext } from '../../../../../hooks/PageContext/usePageContext';
import flags, { mappedFlags, defaultMask } from './lib/flags';
import { isSmsCountry } from './lib/smsCountry';
import { useTheme } from '../../../../../hooks/useTheme';
import { useGlobalSettings } from '../../../../../hooks/useGlobalSettings';
import styles from './TelephoneInput.module.css';
import { useTranslations } from '../../../../../hooks/useTranslations';

export { isSmsCountry };

export const domesticCountries: SmsCountry[] = ['US', 'CA'];

export interface TelephoneInputProps {
  label?: string;
  title?: string;
  centered?: boolean;
  error?: boolean;
  disabled?: boolean;
  onChangeCallback?: (phoneNumber: string) => void | undefined;
  featureName?: string;
  s?: CSSProperties;
  placeholder?: string;
  ariaLabelledby?: string;
}

// need to add theme error styling
const TelephoneInput: FC<TelephoneInputProps> = ({
  label,
  title = 'Telephone Number',
  centered = false,
  error = false,
  disabled,
  onChangeCallback,
  s = {},
  placeholder,
  ariaLabelledby,
}) => {
  const { smsDefaultCountry, smsSupportedCountries } = useGlobalSettings();
  const { countryDropdownAriaLabel } = useTranslations();
  const { telephoneinputNoneNone: telephoneInputStyle } = useTheme();

  const {
    state: { localeCountry },
  } = usePageContext();

  const defaultCountry: SmsCountry =
    smsDefaultCountry || (isSmsCountry(localeCountry) ? localeCountry : 'US');
  const international = !domesticCountries.includes(defaultCountry);

  const [selectedCountry, setSelectedCountry] = useState(
    mappedFlags[defaultCountry],
  );
  const [number, setNumber] = useState('');
  const [focused, setFocused] = useState(false);

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  useEffect(
    () => {
      const { dialCode, mask = defaultMask } = selectedCountry;
      const phoneNumber = number.replace(/\D/g, '');
      const { conformedValue } = conformToMask(
        phoneNumber.length > 10 ? phoneNumber.substr(0, 10) : phoneNumber,
        mask,
        {
          guide: false,
          previousConformedValue: phoneNumber,
        },
      );

      if (onChangeCallback) {
        onChangeCallback(`${international ? dialCode : ''}${phoneNumber}`);
      }
      setNumber(international || !mask.length ? phoneNumber : conformedValue);
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [number],
  );

  useEffect(() => {
    if (!defaultCountry) return;
    setSelectedCountry(mappedFlags[defaultCountry]);
  }, [defaultCountry]);

  const containerStyle = {
    ...telephoneInputStyle,
    ...(focused ? { borderColor: '#111' } : s),
  };

  const handleCountrySelectChange = (option: SelectOptionProps | null) => {
    const flag = flags.find((flag) => flag.code === (option?.value || 'US'));

    if (!flag) return;

    setSelectedCountry(flag);
  };

  const handleCountrySelectFocus = () => {
    setFocused(true);
  };

  const handleCountrySelectBlur = () => {
    setFocused(false);
  };

  const flagOptions = flags
    .filter(
      ({ code }) =>
        !smsSupportedCountries ||
        !smsSupportedCountries.length ||
        smsSupportedCountries?.includes(code),
    )
    .map(({ code, emoji, name }) => ({
      value: code,
      label: `${emoji} ${name}`,
    }));

  return (
    <div
      className={styles.inputContainer}
      data-testid="telephone-input-container"
      style={containerStyle}
    >
      <div className={styles.tableContainer}>
        <div className={styles.cellContainer}>
          {international && (
            <Select
              name="country-selector"
              options={flagOptions}
              label={countryDropdownAriaLabel ?? ''}
              hideLabel
              value={flagOptions.find(
                (flag) => flag.value === selectedCountry.code,
              )}
              valueRender={selectedCountry.emoji}
              style={{
                container: {
                  margin: 0,
                  width: '3rem',
                  height: '2rem',
                  padding: '0.1875rem',
                },
                control: { border: 0, height: '100%', padding: '0 0.25rem' },
                selected: { fontSize: '1.25rem' },
              }}
              system
              outline
              onChange={handleCountrySelectChange}
              onFocus={handleCountrySelectFocus}
              onBlur={handleCountrySelectBlur}
            />
          )}
        </div>
        <div
          data-test="telephoneInputNumberContainer"
          className={international ? styles.cellInternational : undefined}
        >
          <TextInput
            containerOverridesStyle={{ border: 'none' }}
            label={label}
            id={label}
            type={SupportedInputType.tel}
            value={number}
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              setNumber(event.target.value)
            }
            error={error}
            centered={centered}
            maxLength={14}
            title={title}
            animateLabel
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            ariaLabelledby={ariaLabelledby}
          />
        </div>
      </div>
    </div>
  );
};

export default TelephoneInput;
